import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import { useCurrentUser } from '@/features/auth/hooks/use-current-user'
import type { UserRole } from '@/shared/types'

export interface OrganizationUser {
  id: string
  email: string
  full_name?: string
  avatar_url?: string
  role: UserRole
  created_at: string
  updated_at: string
  teams: {
    id: string
    name: string
    short_id: string
  }[]
}

async function fetchOrganizationUsers(organizationId: string): Promise<OrganizationUser[]> {
  // Fetch users with their team assignments
  const { data: users, error: usersError } = await supabase
    .from('users')
    .select(`
      id,
      email,
      full_name,
      avatar_url,
      role,
      created_at,
      updated_at
    `)
    .eq('organization_id', organizationId)
    .order('full_name', { ascending: true, nullsFirst: false })

  if (usersError) {
    throw new Error(usersError.message)
  }

  if (!users || users.length === 0) {
    return []
  }

  // Fetch team assignments for all users
  const userIds = users.map(u => u.id)
  const { data: userTeams, error: teamsError } = await supabase
    .from('user_teams')
    .select(`
      user_id,
      team:teams(id, name, short_id)
    `)
    .in('user_id', userIds)

  if (teamsError) {
    throw new Error(teamsError.message)
  }

  // Group teams by user
  type TeamData = { id: string; name: string; short_id: string }
  const teamsByUser = new Map<string, OrganizationUser['teams']>()
  for (const ut of userTeams || []) {
    const userId = ut.user_id
    if (!teamsByUser.has(userId)) {
      teamsByUser.set(userId, [])
    }
    if (ut.team) {
      // Supabase returns a single object for 1:1 relationship via foreign key
      const team = ut.team as unknown as TeamData
      teamsByUser.get(userId)!.push(team)
    }
  }

  // Combine users with their teams
  return users.map(user => ({
    ...user,
    teams: teamsByUser.get(user.id) || []
  })) as OrganizationUser[]
}

export function useOrganizationUsers() {
  const { user } = useCurrentUser()

  return useQuery({
    queryKey: ['organizationUsers', user?.organization_id],
    queryFn: () => fetchOrganizationUsers(user!.organization_id!),
    enabled: !!user?.organization_id,
  })
}
